import { Link } from 'react-router-dom';

/**
 * Shared typography + spacing for /learn articles. Uses inline styles to
 * stay consistent with the rest of the site (no global .article CSS class).
 *
 * Article body components should compose <P>, <H2>, <H3>, <UL>, <LI>, <Note>
 * exported below for consistent rhythm.
 */
export default function ArticleLayout({ title, summary, readingMin, date, children, related = [] }) {
  return (
    <article style={{ maxWidth: 720, margin: '0 auto', padding: '32px 20px 64px', fontSize: 16, lineHeight: 1.7, color: 'var(--text-dim)' }}>
      <Link to="/learn" style={{ fontSize: 13, color: 'var(--text-muted)' }}>← All articles</Link>
      <h1 style={{ color: 'var(--text)', fontSize: '2rem', marginTop: 16, marginBottom: 8, lineHeight: 1.25 }}>{title}</h1>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
        {date && <span>{date}</span>}
        {date && readingMin && <span> · </span>}
        {readingMin && <span>{readingMin} min read</span>}
      </div>
      {summary && (
        <p style={{ fontSize: 17, color: 'var(--text)', borderLeft: '3px solid var(--accent)', paddingLeft: 14, margin: '0 0 28px' }}>
          {summary}
        </p>
      )}
      <div>{children}</div>

      {related.length > 0 && (
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
          <h3 style={{ color: 'var(--text)', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Related reading</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {related.map((r) => (
              <li key={r.slug} style={{ marginBottom: 8 }}>
                <Link to={`/learn/${r.slug}`} style={{ color: 'var(--accent)' }}>{r.title}</Link>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{r.summary}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ marginTop: 40, padding: 16, background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', textAlign: 'center' }}>
        <div style={{ fontSize: 14, color: 'var(--text)', marginBottom: 8 }}>TrainForge applies this in your program automatically.</div>
        <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 600 }}>Sign in or get started →</Link>
      </div>
    </article>
  );
}

export const P = ({ children }) => <p style={{ margin: '0 0 16px' }}>{children}</p>;
export const H2 = ({ children }) => <h2 style={{ color: 'var(--text)', fontSize: '1.4rem', marginTop: 32, marginBottom: 12 }}>{children}</h2>;
export const H3 = ({ children }) => <h3 style={{ color: 'var(--text)', fontSize: '1.1rem', marginTop: 24, marginBottom: 8 }}>{children}</h3>;
export const UL = ({ children }) => <ul style={{ margin: '0 0 16px', paddingLeft: 22 }}>{children}</ul>;
export const OL = ({ children }) => <ol style={{ margin: '0 0 16px', paddingLeft: 22 }}>{children}</ol>;
export const LI = ({ children }) => <li style={{ marginBottom: 6 }}>{children}</li>;
export const Strong = ({ children }) => <strong style={{ color: 'var(--text)' }}>{children}</strong>;
export const Note = ({ children }) => (
  <div style={{ background: 'var(--bg-elev)', borderLeft: '3px solid var(--accent)', padding: '12px 14px', borderRadius: 'var(--radius-md)', margin: '0 0 16px', fontSize: 15 }}>
    {children}
  </div>
);
